import React, { useState, useEffect } from 'react';
import {
  GraduationCap,
  Wrench,
  Store,
  Users,
  Menu,
  LogIn,
  Rocket,
  ArrowRight,
  Instagram,
  Youtube,
  Mail,
  HelpCircle,
  ChevronRight,
  Loader2
} from 'lucide-react';
import {
  UserState,
  AcademyConfig,
  defaultConfig,
  getUserState,
  applyTheme,
  clearUserSession
} from '../../config/academyConfig';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useProgress } from '../../hooks/useProgress';

// Academy Components
import AcademyModules from './AcademyModules';
import ProgressTracker from './ProgressTracker';
import ResourceLibrary from './ResourceLibrary';
import MentoringCalendar from './MentoringCalendar';

// Vendor Components
import VendorDashboard from '../vendor/VendorDashboard';

// Shared Components
import CommunityFeed from '../shared/CommunityFeed';
import LoginModal from '../shared/LoginModal';
import StartTrialCTA from '../shared/StartTrialCTA';
import SearchBar from '../shared/SearchBar';

// Fallback URL if API fails
const FALLBACK_URL = 'https://myshop.coffee/product/founding-partner/';

type TabType = 'learn' | 'tools' | 'store' | 'community' | 'more';

interface AcademyAppProps {
  config?: AcademyConfig;
}

const TAB_ICONS = {
  learn: GraduationCap,
  tools: Wrench,
  store: Store,
  community: Users,
  more: Menu
};

const AcademyApp: React.FC<AcademyAppProps> = ({ config = defaultConfig }) => {
  const [activeTab, setActiveTab] = useState<TabType>('learn');
  const [userState, setUserState] = useState<UserState>(() => getUserState());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const { bookmarks, toggleBookmark } = useBookmarks();
  const { progress, setCurrentModule } = useProgress();

  const handleStartTrial = async () => {
    setIsCheckoutLoading(true);

    try {
      // Call our Vercel proxy (avoids CORS)
      const response = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        window.location.href = FALLBACK_URL;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      window.location.href = FALLBACK_URL;
    }
  };

  useEffect(() => {
    // Apply theme on mount
    applyTheme(config);

    // Check auth state
    const state = getUserState();
    setUserState(state);
    setIsLoading(false);

    // Handle OAuth callbacks
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('vendor_token') || urlParams.get('shopify_token')) {
      handleOAuthCallback(urlParams);
    }
  }, [config]);

  const handleOAuthCallback = async (params: URLSearchParams) => {
    const vendorToken = params.get('vendor_token');
    const shopifyToken = params.get('shopify_token');

    if (vendorToken) {
      console.log('Processing MyShop vendor OAuth callback');
    }

    if (shopifyToken) {
      console.log('Processing Shopify OAuth callback');
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleLogin = (userData: UserState) => {
    setUserState(userData);
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    clearUserSession();
    setUserState({ mode: 'guest', isAuthenticated: false });
    setActiveTab('learn');
  };

  const handleSearchResult = (result: { id: string; type: 'module' | 'resource' }) => {
    if (result.type === 'module') {
      setActiveTab('learn');
    } else {
      setActiveTab('tools');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'learn':
        return (
          <div className="space-y-6 p-4">
            <ProgressTracker
              progress={progress}
              isAuthenticated={userState.isAuthenticated}
              onLoginClick={() => setShowLoginModal(true)}
            />
            <AcademyModules
              isAuthenticated={userState.isAuthenticated}
              completedModules={progress.modulesCompleted}
              bookmarkedModules={bookmarks.filter(b => b.type === 'module').map(b => b.id)}
              onModuleClick={(moduleId) => {
                if (!userState.isAuthenticated) {
                  setShowLoginModal(true);
                } else {
                  setCurrentModule(moduleId);
                }
              }}
              onToggleBookmark={(moduleId, title) => toggleBookmark(moduleId, 'module', title)}
            />
          </div>
        );

      case 'tools':
        return (
          <ResourceLibrary
            isAuthenticated={userState.isAuthenticated}
            onLoginClick={() => setShowLoginModal(true)}
            bookmarkedResources={bookmarks.filter(b => b.type === 'resource').map(b => b.id)}
            onToggleBookmark={(resourceId, title) => toggleBookmark(resourceId, 'resource', title)}
          />
        );

      case 'store':
        if (userState.mode === 'vendor' && userState.isAuthenticated) {
          return <VendorDashboard vendorId={userState.vendorId} />;
        }
        return (
          <StartTrialCTA
            isAuthenticated={userState.isAuthenticated}
            onLoginClick={() => setShowLoginModal(true)}
          />
        );

      case 'community':
        return (
          <div className="space-y-6 p-4">
            <CommunityFeed />
            <MentoringCalendar
              isAuthenticated={userState.isAuthenticated}
              onLoginClick={() => setShowLoginModal(true)}
            />
          </div>
        );

      case 'more':
        return (
          <div className="min-h-full bg-neutral-50 px-5 py-6">
            <h1 className="text-2xl font-bold text-neutral-900 mb-6">Settings</h1>

            {/* Account Section */}
            <div className="mb-6">
              <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">Account</p>
              {userState.isAuthenticated ? (
                <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
                  <div className="p-4 flex items-center">
                    <div className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center text-white font-semibold mr-3">
                      {(userState.name || userState.email || 'U')[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-neutral-900 text-sm">{userState.name || 'User'}</p>
                      <p className="text-xs text-neutral-400">{userState.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full p-4 text-left text-sm text-red-500 font-medium border-t border-neutral-100 hover:bg-neutral-50 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="w-full bg-neutral-900 text-white p-4 rounded-2xl font-semibold text-sm flex items-center justify-center transition-all hover:bg-neutral-800 active:scale-[0.99]"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </button>
              )}
            </div>

            {/* Connect Section */}
            <div className="mb-6">
              <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">Connect</p>
              <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden divide-y divide-neutral-100">
                <a
                  href={config.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center mr-3">
                    <Instagram className="w-5 h-5 text-neutral-600" />
                  </div>
                  <span className="flex-1 font-medium text-sm text-neutral-900">Instagram</span>
                  <ChevronRight className="w-4 h-4 text-neutral-300" />
                </a>
                <a
                  href={config.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center mr-3">
                    <Users className="w-5 h-5 text-neutral-600" />
                  </div>
                  <span className="flex-1 font-medium text-sm text-neutral-900">Community</span>
                  <ChevronRight className="w-4 h-4 text-neutral-300" />
                </a>
                {config.socialLinks.youtube && (
                  <a
                    href={config.socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-4 hover:bg-neutral-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center mr-3">
                      <Youtube className="w-5 h-5 text-neutral-600" />
                    </div>
                    <span className="flex-1 font-medium text-sm text-neutral-900">YouTube</span>
                    <ChevronRight className="w-4 h-4 text-neutral-300" />
                  </a>
                )}
              </div>
            </div>

            {/* Support Section */}
            <div className="mb-6">
              <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">Support</p>
              <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden divide-y divide-neutral-100">
                <a
                  href="mailto:hello@yourbeans.com.au"
                  className="flex items-center p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center mr-3">
                    <Mail className="w-5 h-5 text-neutral-600" />
                  </div>
                  <span className="flex-1 font-medium text-sm text-neutral-900">Contact</span>
                  <ChevronRight className="w-4 h-4 text-neutral-300" />
                </a>
                <a
                  href="https://yourbeans.com.au/faq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center mr-3">
                    <HelpCircle className="w-5 h-5 text-neutral-600" />
                  </div>
                  <span className="flex-1 font-medium text-sm text-neutral-900">FAQ</span>
                  <ChevronRight className="w-4 h-4 text-neutral-300" />
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-4">
              <a
                href="https://numerounocoffeeroasters.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                Powered by Numero Uno Coffee Roasters
              </a>
              <p className="text-xs text-neutral-300 mt-2">Version 1.0.0</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img
            src={config.logo}
            alt="YourBeans"
            className="h-16 w-auto mx-auto mb-4"
          />
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
            style={{ borderColor: config.primaryColor }}
          />
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="app-container w-full bg-gray-50 flex flex-col relative">
        {/* Header */}
        <header className="app-header sticky top-0 z-40 bg-white shadow-sm px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img src={config.logo} alt="YourBeans" className="h-8 w-auto" />
            <span className="ml-2 font-semibold text-gray-900">
              Coffee Academy
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <SearchBar onResultClick={handleSearchResult} />
            {userState.isAuthenticated ? (
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-semibold"
                style={{ backgroundColor: config.primaryColor }}
              >
                {(userState.name || userState.email || 'U')[0].toUpperCase()}
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-xl transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: config.primaryColor }}
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Sign In
              </button>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto pb-24">
          {renderTabContent()}
        </main>

        {/* Persistent CTA Banner (for non-vendors) */}
        {userState.mode !== 'vendor' && (
          <div
            className="app-cta-banner fixed bottom-16 left-0 right-0 text-white p-3 text-center text-sm z-30 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${config.secondaryColor} 0%, ${config.primaryColor} 100%)`
            }}
          >
            {isCheckoutLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading checkout...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4 mr-2" />
                Ready to start your coffee business?{' '}
                <button
                  onClick={handleStartTrial}
                  className="underline font-semibold ml-1 flex items-center bg-transparent border-none cursor-pointer text-white"
                >
                  Start Free Trial
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Bottom Tab Bar */}
        <nav className="app-nav fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
          <div className="flex justify-around items-center h-16">
            {[
              { id: 'learn', label: 'Learn' },
              { id: 'tools', label: 'Tools' },
              { id: 'store', label: 'Store' },
              { id: 'community', label: 'Community' },
              { id: 'more', label: 'More' }
            ].map((tab) => {
              const Icon = TAB_ICONS[tab.id as keyof typeof TAB_ICONS];
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
                    activeTab === tab.id ? '' : 'text-gray-500'
                  }`}
                  style={activeTab === tab.id ? { color: config.primaryColor } : {}}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs mt-1">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Login Modal */}
        {showLoginModal && (
          <LoginModal
            config={config}
            onClose={() => setShowLoginModal(false)}
            onLogin={handleLogin}
          />
        )}
      </div>
    </div>
  );
};

export default AcademyApp;
